import { RawDraftContentState } from 'draft-js';
import { useEffect, useMemo, useRef, useState } from 'react';
import { useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { toast } from 'react-toastify';
import { useMutation } from 'urql';
import { Button } from '~/components/form/SubmitButton';
import { Input } from '~/components/form/TextField';
import { Dialog } from '~/components/ui/Dialog';
import { TaskInput } from '~/generated/graphql';
import { graphql } from '~/gql';
import { Status } from '~/gql/graphql';
import { toInput } from '../job-template/JobTemplateForm';
import { Pill } from '../ui/nucleus/Pill';
import { UploadResult } from '../upload2';

const CreateJobTemplateDocument = graphql(`
  mutation CreateJobTemplate($input: JobTemplateInput!) {
    createJobTemplate(input: $input) {
      id
    }
  }
`);

type FormValues = {
  name: string;
};

type Props = {
  jobName: string;
  tasks: TaskInput[];
  notes?: RawDraftContentState | null;
  tags?: string[] | null;
  includeAttributes?: string[] | null;
  jobAttributes?: string[] | null;
  attachments?: UploadResult[];
  guestyCleaningStatus?: boolean;
  onSuccess?: (templateId: string, templateName: string) => void;
};

export const CreateTemplateButton = ({
  jobName,
  tasks,
  notes,
  tags,
  includeAttributes,
  jobAttributes,
  attachments,
  guestyCleaningStatus,
  onSuccess,
}: Props) => {
  const { t } = useTranslation();
  const [showDialog, setShowDialog] = useState(false);
  const [, createTemplate] = useMutation(CreateJobTemplateDocument);
  const createdSignatureRef = useRef<string | null>(null);

  const {
    register,
    reset,
    handleSubmit,
    formState: { errors, isSubmitting },
  } = useForm<FormValues>({
    defaultValues: {
      name: jobName || '',
    },
  });

  // Update default value when jobName changes
  useEffect(() => {
    if (showDialog) {
      reset({ name: jobName || '' });
    }
  }, [showDialog, jobName, reset]);

  const signature = useMemo(
    () =>
      JSON.stringify({
        tasks,
        notes,
        tags,
        includeAttributes,
        jobAttributes,
        attachments,
        guestyCleaningStatus,
      }),
    [
      tasks,
      notes,
      tags,
      includeAttributes,
      jobAttributes,
      attachments,
      guestyCleaningStatus,
    ]
  );

  const onSubmit = handleSubmit(async (data) => {
    try {
      const values = {
        name: data.name,
        status: Status.Active,
        notes: notes ?? null,
        tags: tags || null,
        includeAttributes: includeAttributes || null,
        jobAttributes: jobAttributes || null,
        attachments: attachments || [],
        guestyCleaningStatus,
      };

      const input = toInput(tasks, values);

      // Use direct GraphQL mutation instead of form submission
      const result = await createTemplate({ input });

      if (result.error || !result.data) {
        console.error(
          'CreateTemplateButton createTemplate error:',
          result.error
        );
        toast.error(
          result.error?.message?.replace('[GraphQL] ', '') ||
            'An unexpected error occurred while creating the template'
        );
        return;
      }

      // Record the signature at the moment of creation to hide the button
      createdSignatureRef.current = signature;
      setShowDialog(false);
      onSuccess?.(result.data.createJobTemplate.id, data.name);
    } catch (error) {
      console.error('CreateTemplateButton createTemplate catch:', error);
      toast.error('An unexpected error occurred while creating the template');
    }
  });

  // Don't render button if template was just created (until form changes)
  if (createdSignatureRef.current === signature) {
    return null;
  }

  return (
    <>
      <Dialog
        title={t('translation:newTemplate')}
        show={showDialog}
        onClose={() => setShowDialog(false)}
      >
        <form onSubmit={onSubmit} className='p-3 text-left'>
          <div className='mb-4'>
            <Input
              {...register('name', {
                required: 'Template name is required',
              })}
              label={t('translation:templateName')}
              required
              error={errors.name?.message}
              autoFocus
              maxLength={32}
            />
          </div>

          <div className='flex justify-end gap-3'>
            <button
              type='button'
              className='px-4 py-2 text-sm font-medium uppercase text-brand-dark transition-all duration-500 hover:text-brand'
              onClick={() => setShowDialog(false)}
              disabled={isSubmitting}
            >
              {t('translation:cancel')}
            </button>

            <Button
              type='submit'
              intent='primary'
              size='small'
              disabled={isSubmitting}
            >
              {isSubmitting ? 'Creating...' : t('translation:create')}
            </Button>
          </div>
        </form>
      </Dialog>
      <div className='mb-5 flex items-center justify-center'>
        <Pill hasParent onClick={() => setShowDialog(true)} active={showDialog}>
          Create Template from Job
        </Pill>
      </div>
    </>
  );
};
